import { FileText } from "lucide-react";
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import { cn } from '../../../lib/utils';

import FeatureGate from "../../featureFlags/components/FeatureGate";
import MenuDropdownButton from "../../../components/ui/button/v2/MenuDropdownButton";
import DataMatrixTable from "../../../components/ui/datatable/DataMatrixTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs/Tabs';

import InvestigationReportSkeleton from './IRSkeleton';

import { ReportReviewPayload, ReviewStatusEnum, UserActionType } from "src/api/UserActionsStatus";
import ReviewerButton from '../../../components/ui/button/ReviewerButton';
import { StickyBar } from "../../../components/ui/stickybar/StickyBar";
import { ReviewerManagerDialog } from '../../userActionsStatus/components/ReviewerManagerDialog';
import { EvidenceReport } from './IREvidences';

import { CommentEntityType, CommentThreadType } from "../../../api/comments";
import { ReportType } from "../../../api/investigationReport";
import { useAppSelector } from '../../../app/hooks';
import { useAppDispatch } from '../../../app/store';
import CansoEditor from "../../../components/ui/editor";
import { useInvestigationReport } from "../../../hooks/useInvestigationReport";
import { ExcelDownloadService } from '../../../services/ExcelDownloadService';
import { ReportDataService } from '../../../services/IRReportDataService';
import CommentMainPage from "../../comments/components/CommentMainPage";
import { selectIRActiveReviews, selectIsIRFinalized, selectUserActionsStatusSaving } from '../../userActionsStatus/logic/userActionsStatusSlice';
import { fetchUserActionsStatusThunk, updateMultipleIRReviewStatusesThunk } from "../../userActionsStatus/logic/userActionsStatusThunk";
import { getInvestigators, getReviewers } from "../../users/logic/slice";
import InvestigationDocuments from "./InvestigationDocuments";
import { IOSubmitIRStageButton } from "../../caseStages/components/stage_ctas/IOSubmitIRStageButton";
import { isInvestigationReportDownloadEnabled } from "../../configs/logic/Slice";
import { ReportCoverLetterModal } from "./ReportCoverLetterModal";




const UNAVAILABLE_TEXT = 'Data Not Available';

const REPORT_DOWNLOAD_OPTIONS = [
    { name: 'Preliminary / Flash Report', value: ReportType.PRELIMINARY_REPORT },
    { name: 'Investigation Report', value: ReportType.INVESTIGATION_REPORT },
];

type ProcessedKeyValueTabData = {
    fields: { key: string; field: string; value: string | null }[];
};

type ProcessedMultiColumnTabData = {
    headers: string[];
    rows: { title: string; values: (string | number | null)[] }[];
};

type ProcessedTab =
    | {
        tabName: string;
        layout: 'key-value';
        data: ProcessedKeyValueTabData;
    }
    | {
        tabName: string;
        layout: 'multi-column';
        data: ProcessedMultiColumnTabData;
    }
    | {
        tabName: string;
        layout: 'evidence';
        data: any;
    } | {
        tabName: string;
        layout: 'recommendations';
    } 
    | {
        tabName: string;
        layout: 'chronology';
        data: any;
    };


const KeyValueReport: React.FC<{ data: { key: string; field: string; value: string | null }[] }> = ({ data }) => {
    if (!data || data.length === 0) {
        return <div className="text-center py-8 text-gray-500">No data available for this section.</div>;
    }

    return (
        <div className="bg-white border border-[#E5E5E5] rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-100">
                <div className="grid grid-cols-12 gap-4 px-2 py-2">
                    <div className="col-span-4 md:col-span-3">
                        <span className="text-sm font-medium text-[#737373]">Details</span>
                    </div>
                    <div className="col-span-8 md:col-span-9">
                        <span className="text-sm font-medium text-[#737373]">Value</span>
                    </div>
                </div>
            </div>
            <div className="divide-y divide-[#E5E5E5]">
                {data.map((field, index) => (
                    <div
                        key={field.key}
                        className={cn(
                            "grid grid-cols-12 gap-4 px-2 py-4 transition-all duration-200",
                            index % 2 === 0 ? "bg-white" : "bg-gray-25"
                        )}
                    >
                        <div className="col-span-4 md:col-span-3 flex items-center">
                            <span className="text-sm font-medium text-[#0A0A0A]">{field.field}</span>
                        </div>
                        <div className="col-span-8 md:col-span-9 flex items-center">
                            <span className={cn(
                                "text-sm",
                                field.value === UNAVAILABLE_TEXT
                                    ? "text-gray-400 font-normal italic"
                                    : "text-[#0A0A0A] font-normal"
                            )}>
                                {field.value}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const RecommendationsTab: React.FC<{ tab: any }> = ({ tab }) => {
  const personTitle =
    tab.data?.personInvolvedConfig?.title ?? "Actions Against Involved Parties";
  const policyTitle =
    tab.data?.policyImprovementConfig?.title ?? "Process & Policy Improvements";

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold text-[#0A0A0A]">
                  {personTitle}
                </div>
                <DataMatrixTable data={tab.data?.personInvolvedConfig} />
            </div>
            <div className="flex flex-col gap-2">
                <div className="text-sm font-semibold text-[#0A0A0A]">
                  {policyTitle}
                </div>
                <DataMatrixTable data={tab.data?.policyImprovementConfig} />
            </div>
        </div>
    );
};


const ChronologyReport: React.FC<{ data: { title: string; key: string; value: string | null }[] }> = ({ data }) => {

    // transform all data into single editor content like title in h2 and bold, value as normal text then 2/3 new lines
    const transformedData = useMemo(() => {
        if (!data || data.length === 0) {
            return '';
        }
        return data.map(remark => {
            return `## ${remark.title}\n\n${remark.value || ''}\n\n\n\n`;
        }).join('\n\n');
    }, [data]);

    if (!data || data.length === 0) {
        return <div className="text-center py-8 text-gray-500">No chronology data available.</div>;
    }

    return (
        <div className="space-y-6 border border-gray-200/60 shadow-sm rounded-lg bg-white">
            <CansoEditor
                markdown={transformedData}
                readOnly={true}
                id='chronology-overview-editor'
                placeholder="No chronology data available."
                contentEditableClassName='h-auto'
            />
        </div>
    );
};


export const InvestigationReportPage: React.FC = () => {
    const { id: caseId } = useParams<{ id: string }>();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const rawData = useInvestigationReport();
    const [isReviewerDialogOpen, setReviewerDialogOpen] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(null);

    useEffect(() => {
        if (caseId) {
            dispatch(fetchUserActionsStatusThunk({ case_id: caseId }));
        }
    }, [caseId, dispatch]);

    const activeReviewers = useAppSelector(selectIRActiveReviews);
    const isApproved = useAppSelector(selectIsIRFinalized);
    const isSaving = useAppSelector(selectUserActionsStatusSaving);
    const availableReviewers = useAppSelector(getReviewers);
    const investigators = useAppSelector(getInvestigators);
    const currentUserEmail = auth?.user?.profile?.email ?? null;

    // check if the current user is one of the activeReviewers and it is not already approved by them
    const isCurrentUserAnActiveReviewer = useMemo(() => {
        // Ensure we have the necessary data before checking
        if (!currentUserEmail || !activeReviewers) {
            return false;
        }

        // We check if any of the 'approver' fields in that array match the current user's email.
        const activeReviewer = activeReviewers.find(review => review?.review_status?.approver === currentUserEmail);

        return activeReviewer && activeReviewer?.review_status?.status === ReviewStatusEnum.ASSIGNED;

    }, [currentUserEmail, activeReviewers]);

    const getAssigneeNameFromTheReview = () => {
        const reviewerAssignee = activeReviewers.find(reviewer => reviewer?.review_status?.assigned_by === currentUserEmail);
        return investigators.find(inv => inv.email_id === reviewerAssignee?.review_status?.assigned_by)?.user_name;
    }

    const approverBtnText = isSaving ? "Approving..." : "Mark As Approved";
    const reviewerBtnText = isApproved ? "Approved" : activeReviewers.length > 0 ? "Reviewers" : "Add Reviewers";
    const reviewerBtnVariant = isApproved ? 'approved' : 'reviewer';
    const processedTabs = useMemo((): ProcessedTab[] => {
        if (rawData.isLoading) return [];
        return ReportDataService.processAllTabs(rawData);
    }, [rawData]);


    const handleSaveReviewers = (newReviewers: ReportReviewPayload[]) => {
        if (caseId && currentUserEmail) {
            const reviewersData = newReviewers.map(nr => ({
                ...nr,
                review_status: {
                    ...nr.review_status,
                    status: ReviewStatusEnum.ASSIGNED,
                    assigned_by: currentUserEmail,
                    assigned_at: new Date().toISOString()
                }
            }));

            dispatch(updateMultipleIRReviewStatusesThunk({
                case_id: caseId,
                reviewers: reviewersData
            }))
                .unwrap()
                .then(() => {
                    setReviewerDialogOpen(false); // Close dialog on success
                });
        }
    };

    const handleOnApprove = () => {
        if (caseId && currentUserEmail) {
            const reviewerToUpdate = activeReviewers.find(review => review?.review_status?.approver === currentUserEmail);
            if (!reviewerToUpdate) return;

            // Create a new object with the updated status to signal the approval action.
            const approverData = {
                ...reviewerToUpdate,
                review_status: {
                    ...reviewerToUpdate.review_status,
                    status: ReviewStatusEnum.APPROVED,
                    approved_at: new Date().toISOString()
                }
            };

            dispatch(updateMultipleIRReviewStatusesThunk({
                case_id: caseId,
                reviewers: [approverData]
            }))
                .unwrap()
                .then(() => {
                    // TODO: Show a toast for success
                });
        }
    }

    const handleReportSelect = (value: ReportType) => {
        setSelectedReportType(value);
    };


    if (rawData.isLoading) {
        return (
            <div className="relative w-full h-full">
                <InvestigationReportSkeleton />
            </div>
        );
    }

    // if (rawData.hasError) {
    //     return (
    //         <div className="w-full h-full flex items-center justify-center p-8">
    //             <ErrorDisplay
    //                 error={rawData.caseInfoData.error}
    //                 title="Failed to Load Case Data"
    //                 className="max-w-md"
    //             />
    //         </div>
    //     );
    // }

    return (
      <div className="w-full h-full overflow-auto bg-background">
        <div className="max-w-8xl mx-6 mt-6 border border-gray-100 shadow-overviewCard rounded-xl gap-4">
          <div className="py-4 px-4 bg-[#F9FAFB] rounded-tl-xl rounded-tr-xl">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-foreground">
                Investigation Report
              </h3>
              <div className="flex items-center gap-2">
                <ReviewerButton
                  text={reviewerBtnText}
                  onClick={() => setReviewerDialogOpen(true)}
                  variant={reviewerBtnVariant}
                />
                <FeatureGate selector={isInvestigationReportDownloadEnabled}>
                  <MenuDropdownButton
                    label="Download"
                    options={REPORT_DOWNLOAD_OPTIONS}
                    onSelect={handleReportSelect}
                  />
                </FeatureGate>
                <IOSubmitIRStageButton />
              </div>
            </div>
          </div>
          <Tabs defaultValue={processedTabs[0]?.tabName || ""} className="">
            {/* Tab Navigation is now driven by the processed data */}
            <TabsList className="w-full sticky top-[0px] z-10 overflow-x-auto backdrop-blur-xl bg-[#F9FAFB] justify-start rounded-none p-0 border-b border-[#E5E5E5]">
              {processedTabs.map((tab) => (
                <TabsTrigger
                  key={tab.tabName}
                  value={tab.tabName}
                  className="text-sm font-normal data-[state=active]:bg-transparent data-[state=active]:shadow-0 px-4"
                >
                  {tab.tabName}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="px-4 pb-4">
              {processedTabs.map((tab) => {
                switch (tab.layout) {
                  case "key-value":
                    return (
                      <TabsContent key={tab.tabName} value={tab.tabName}>
                        <KeyValueReport data={tab.data.fields} />
                      </TabsContent>
                    );
                  case "multi-column":
                    return (
                      <TabsContent key={tab.tabName} value={tab.tabName}>
                        <DataMatrixTable data={tab.data} />
                      </TabsContent>
                    );
                  case "recommendations":
                    return (
                      <TabsContent key={tab.tabName} value={tab.tabName}>
                        <RecommendationsTab tab={tab} />
                      </TabsContent>
                    );
                  case "evidence":
                    return (
                      <TabsContent key={tab.tabName} value={tab.tabName}>
                        <EvidenceReport
                          data={
                            rawData.caseInterviewFindingsData?.interviews || []
                          }
                        />
                      </TabsContent>
                    );

                  case "chronology":
                    return (
                      <TabsContent key={tab.tabName} value={tab.tabName}>
                        <ChronologyReport data={tab.data.remarks} />
                      </TabsContent>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          </Tabs>
        </div>

        {selectedReportType && (
          <ReportCoverLetterModal
            reportType={selectedReportType}
            onClose={() => setSelectedReportType(null)}
          />
        )}

        {caseId && currentUserEmail && (
          <ReviewerManagerDialog
            isOpen={isReviewerDialogOpen}
            onClose={() => setReviewerDialogOpen(false)}
            initialReviewers={activeReviewers}
            availableReviewers={availableReviewers}
            onSave={handleSaveReviewers}
            isSaving={isSaving}
            currentUserEmail={currentUserEmail}
            title="Manage Reviewers"
            levels={[
              UserActionType.IR_L1_REVIEW,
              UserActionType.IR_L2_REVIEW,
              UserActionType.IR_L3_REVIEW,
            ]}
          />
        )}
        {isCurrentUserAnActiveReviewer && (
          <StickyBar className="bottom-6 left-1/2">
            <div className="flex items-center gap-2 text-white">
              <FileText className="w-4 h-4 text-slate-200" />
              <span>
                <span className="font-medium">
                  {getAssigneeNameFromTheReview()}
                </span>{" "}
                Invited to review this Investigation Report
              </span>
            </div>
            <ReviewerButton
              text={approverBtnText}
              onClick={handleOnApprove}
              variant="approved"
              disabled={isSaving}
            />
          </StickyBar>
        )}
       {!rawData.isLoading && caseId && <InvestigationDocuments caseId={caseId} />}
        {!rawData.isLoading && caseId && (
          <CommentMainPage
            caseId={caseId}
            threadType={CommentThreadType.INVESTIGATION_REPORT}
            entityId={caseId}
            entityType={CommentEntityType.CASE}
            className="mx-6"
          />
        )}
        <div className="h-14"></div>
      </div>
    );
};

export default InvestigationReportPage;
